import {
  getComparisonDateRange,
  getDateRangeForPeriod,
  isNamedDatePeriod,
  type DateRangeResolver,
} from '@/lib/date-periods';
import { parseYmd, toYmd } from '@/reports/reportDateUtils';

/**
 * A page's date range in the URL. A named period ("last30days") is stored as `period` alone and
 * recomputed from today whenever the page opens, so a link or saved view keeps rolling. Only a
 * custom range is stored as fixed `from` and `to` dates.
 */
export type DateRangeParams = {
  /** A named period, or 'custom'. */
  period: string;
  /** YYYY-MM-DD */
  from: string;
  /** YYYY-MM-DD */
  to: string;
};

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A YYYY-MM-DD string as a local date, or null when it isn't a real calendar day. */
export function parseUrlYmd(raw: string | null | undefined): Date | null {
  const match = YMD_RE.exec(raw?.trim() ?? '');
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  // Round trip, so 2026-02-31 doesn't roll over into March.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** A named period's dates as of today (or `resolve`'s idea of them). */
export function namedDateRange(
  period: string,
  options: { today?: Date; resolve?: DateRangeResolver } = {}
): DateRangeParams {
  const { start, end } = options.resolve
    ? options.resolve(period)
    : getDateRangeForPeriod(period, options.today);
  return { period, from: toYmd(start), to: toYmd(end) };
}

/**
 * Read `period`, `from` and `to`.
 * - A named period is recomputed and any `from`/`to` beside it are ignored.
 * - Valid `from` <= `to` with no period, 'custom' or an unknown name is a custom range.
 * - Anything else gives `defaultPeriod`, recomputed.
 */
export function readDateRangeParams(
  params: URLSearchParams,
  defaultPeriod: string,
  options: { today?: Date; resolve?: DateRangeResolver } = {}
): DateRangeParams {
  const period = params.get('period');
  if (isNamedDatePeriod(period)) return namedDateRange(period, options);
  const from = parseUrlYmd(params.get('from'));
  const to = parseUrlYmd(params.get('to'));
  if (from && to && from.getTime() <= to.getTime()) {
    return { period: 'custom', from: toYmd(from), to: toYmd(to) };
  }
  return namedDateRange(defaultPeriod, options);
}

/** Write a named period as `period` alone, and anything else as `from` and `to`. */
export function writeDateRangeParams(params: URLSearchParams, range: DateRangeParams): void {
  if (isNamedDatePeriod(range.period)) {
    params.set('period', range.period);
    params.delete('from');
    params.delete('to');
  } else {
    params.delete('period');
    params.set('from', range.from);
    params.set('to', range.to);
  }
}

/**
 * Comparison dates for a range (SKU stock). A custom range keeps valid `comparisonFrom` and
 * `comparisonTo` from the URL; otherwise they're worked out from the range, so they roll with a
 * named period.
 */
export function readComparisonParams(
  params: URLSearchParams,
  range: DateRangeParams,
  comparisonType: string
): { from: string; to: string } {
  if (comparisonType === 'none') return { from: '', to: '' };
  if (!isNamedDatePeriod(range.period)) {
    const from = parseUrlYmd(params.get('comparisonFrom'));
    const to = parseUrlYmd(params.get('comparisonTo'));
    if (from && to && from.getTime() <= to.getTime()) {
      return { from: toYmd(from), to: toYmd(to) };
    }
  }
  const derived = getComparisonDateRange(parseYmd(range.from), parseYmd(range.to), comparisonType);
  return derived ? { from: toYmd(derived.start), to: toYmd(derived.end) } : { from: '', to: '' };
}
